import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo } from 'react';
import { addDays, isSameDay, startOfDay, isBefore } from 'date-fns';
import { findLast } from 'lodash-es';
import { IconButton, KeyboardDatePicker, TextButton } from 'seagull';
import { ChevronLeft, ChevronRight } from 'seagull/icons';
import { getMaxFutureInvoiceDate } from '~/business-logic/billingLogic';
import { useIoContext } from '~/contexts';
import styles from './InvoiceNavigator.module.scss';
function NavigationButtons(props) {
    const { options, value, onChange } = props;
    const is1stSelected = useMemo(()=>isSameDay(value, options[0]), [
        value,
        options
    ]);
    const nextOption = useMemo(()=>options.find((opt)=>isBefore(value, opt)), [
        value,
        options
    ]);
    const prevOption = useMemo(()=>findLast(options, (opt)=>isBefore(opt, value)), [
        value,
        options
    ]);
    const prevDisabled = !prevOption;
    const nextDisabled = !nextOption;
    return /*#__PURE__*/ _jsxs("div", {
        className: styles.buttons,
        children: [
            /*#__PURE__*/ _jsx(IconButton, {
                noPadding: true,
                disabled: prevDisabled,
                tooltip: {
                    title: /*#__PURE__*/ _jsx("div", {
                        "data-testid": "to-prev-tooltip",
                        children: prevDisabled ? 'No earlier invoices are available' : 'Previous invoice'
                    }),
                    placement: 'top'
                },
                testId: "to-prev-invoice",
                onClick: ()=>prevOption && onChange(prevOption),
                children: /*#__PURE__*/ _jsx(ChevronLeft, {})
            }),
            /*#__PURE__*/ _jsx(IconButton, {
                noPadding: true,
                disabled: nextDisabled,
                tooltip: {
                    title: /*#__PURE__*/ _jsx("div", {
                        "data-testid": "to-next-tooltip",
                        children: nextDisabled ? 'No later invoices are available' : 'Next invoice'
                    }),
                    placement: 'top'
                },
                testId: "to-next-invoice",
                onClick: ()=>nextOption && onChange(nextOption),
                children: /*#__PURE__*/ _jsx(ChevronRight, {})
            }),
            !is1stSelected && /*#__PURE__*/ _jsx(TextButton, {
                underline: "none",
                testId: "to-upcoming-invoice",
                onClick: ()=>onChange(options[0]),
                children: "Go to upcoming invoice"
            })
        ]
    });
}
export default function InvoiceNavigator(props) {
    const { value, options, onChange } = props;
    const { getNow } = useIoContext();
    const minDate = useMemo(()=>startOfDay(addDays(getNow(), 1)), []);
    const maxDate = useMemo(()=>getMaxFutureInvoiceDate(getNow()), []);
    function isDayInOptions(day) {
        return !!day && options.some((opt)=>isSameDay(opt, day));
    }
    return /*#__PURE__*/ _jsxs("div", {
        className: styles.root,
        children: [
            /*#__PURE__*/ _jsx(KeyboardDatePicker, {
                size: "small",
                disableErrors: true,
                label: "Billing date",
                testId: "invoice-picker",
                value: value,
                minDate: minDate,
                maxDate: maxDate,
                highlightDay: isDayInOptions,
                onChange: (date)=>onChange(date || null)
            }),
            value && /*#__PURE__*/ _jsx(NavigationButtons, {
                options: options,
                value: value,
                onChange: onChange
            })
        ]
    });
}
